import useApi from "@/hooks/useApi";
import React, { useEffect, useState } from "react";

interface LoginEntry {
  id: number;
  ipAddress: string | null;
  forwardedIp: string | null;
  userAgent: string | null;
  authMethod: string | null;
  acceptLanguage: string | null;
  notificationsGranted: string | null;
  screenResolution: string | null;
  timezone: string | null;
  connectionType: string | null;
  avgResponseMs: number | null;
  platform: string | null;
  loggedInAt: string;
}

interface Props {
  userEmail: string;
}

function parseUserAgent(ua: string | null): { browser: string; os: string } {
  if (!ua) return { browser: "Unknown", os: "Unknown" };

  let browser = "Unknown";
  if (ua.includes("Edg/")) browser = "Edge";
  else if (ua.includes("Chrome/") && !ua.includes("Chromium/")) browser = "Chrome";
  else if (ua.includes("Firefox/")) browser = "Firefox";
  else if (ua.includes("Safari/") && !ua.includes("Chrome/")) browser = "Safari";
  else if (ua.includes("OPR/") || ua.includes("Opera/")) browser = "Opera";
  else if (ua.includes("MSIE") || ua.includes("Trident/")) browser = "IE";

  let os = "Unknown";
  if (ua.includes("Windows NT")) os = "Windows";
  else if (ua.includes("Mac OS X")) os = "macOS";
  else if (ua.includes("Linux") && !ua.includes("Android")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  return { browser, os };
}

function displayIp(entry: LoginEntry): string {
  if (entry.forwardedIp) {
    const first = entry.forwardedIp.split(",")[0].trim();
    if (first) return first;
  }
  return entry.ipAddress ?? "—";
}

function authBadge(method: string | null) {
  if (method === "entra")
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
        Microsoft
      </span>
    );
  if (method === "v10")
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 dark:text-slate-300">
        Email/Password
      </span>
    );
  return <span className="text-slate-400">—</span>;
}

function notifBadge(val: string | null) {
  if (!val) return <span className="text-slate-400 text-xs">—</span>;
  const color =
    val === "granted"
      ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
      : val === "denied"
      ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
      : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
  const label = val === "granted" ? "On" : val === "denied" ? "Off" : val;
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${color}`}>{label}</span>;
}

function connectionBadge(val: string | null) {
  if (!val) return "—";
  const map: Record<string, string> = { "4g": "4G", wifi: "Wi-Fi", "3g": "3G", "2g": "2G", slow: "Slow" };
  return map[val.toLowerCase()] ?? val;
}

export default function AgentLoginHistory({ userEmail }: Props) {
  const api = useApi();
  const [logs, setLogs] = useState<LoginEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    if (!userEmail) return;
    setLoading(true);
    setError(false);
    api.admin
      .get(`AgentLogin?userEmail=${encodeURIComponent(userEmail)}&page=${page}&pageSize=${pageSize}`)
      .then((r) => r.json())
      .then((data) => {
        setLogs(Array.isArray(data?.logs) ? data.logs : []);
        setTotal(data?.total ?? 0);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [userEmail, page, api.admin]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="font-semibold text-lg mb-1">Login History</div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
        Compliance record — IP, browser, auth method, notifications, connection. Hover any row for raw User-Agent.
      </p>

      {loading && <div className="text-sm text-slate-500">Loading...</div>}
      {error && <div className="text-sm text-red-500">Failed to load login history.</div>}

      {!loading && !error && logs.length === 0 && (
        <div className="text-sm text-slate-500 italic">
          No login records yet. History is captured from when this feature was enabled.
        </div>
      )}

      {!loading && !error && logs.length > 0 && (
        <>
          <div className="overflow-x-auto max-h-96 overflow-y-auto rounded border border-slate-200 dark:border-stone-600">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Date / Time (UTC)</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">IP Address</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">Browser</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">OS</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">Auth</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">Notif.</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">Network</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">Latency</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">TZ</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 dark:text-slate-300">Screen</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry, i) => {
                  const { browser, os } = parseUserAgent(entry.userAgent);
                  const rowBg = i % 2 === 0 ? "bg-white" : "bg-slate-50";
                  return (
                    <tr
                      key={entry.id}
                      className={rowBg}
                      title={entry.userAgent ?? undefined}
                    >
                      <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {entry.loggedInAt}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {displayIp(entry)}
                      </td>
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">{browser}</td>
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">{os}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{authBadge(entry.authMethod)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{notifBadge(entry.notificationsGranted)}</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-400 text-xs whitespace-nowrap">
                        {connectionBadge(entry.connectionType)}
                      </td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        {entry.avgResponseMs != null ? (
                          <span
                            className={
                              entry.avgResponseMs > 3000
                                ? "text-red-600 font-semibold"
                                : entry.avgResponseMs > 1000
                                ? "text-yellow-600"
                                : "text-green-600"
                            }
                          >
                            {entry.avgResponseMs} ms
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {entry.timezone
                          ? entry.timezone.replace("America/", "").replace("Europe/", "").replace("Asia/", "")
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {entry.screenResolution ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2 mt-2 text-sm text-slate-600 dark:text-slate-400">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100 dark:border-stone-600 dark:hover:bg-stone-700"
              >
                &laquo; Prev
              </button>
              <span>
                Page {page} of {totalPages} &nbsp;({total} total)
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100 dark:border-stone-600 dark:hover:bg-stone-700"
              >
                Next &raquo;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
